import { firebaseApp } from "firebaseConfig";
import { WEBOT_SERVER_ENDPOINT } from "logic/Consts";
import { presentationEditsForAdd } from "./minisitePage";
import type { ListingPresentationEdits } from "./minisitePage";
import type { REST_schema } from "../../@types/sharedSchema";

/**
 * What an add of a listing carries onto a buyer's page: the realtor's saved presentation of it
 * (`listing-presentations`, which only the server reads), from `GET /listing-presentations/:id` —
 * which, when there is no record yet, seeds it from the realtor's own per-contact edits, as the edit
 * dialog does. Only when the server cannot be reached does it use `fallback` (firestoreUtils passes
 * the scan of the realtor's other pages), so an add still carries something sensible rather than the
 * bare listing.
 *
 * Shared for a minute, in flight included: a bulk message or a matches-table bulk add puts ONE
 * listing on hundreds of pages, and each lookup reads the realtor's pages that hold it. Saving the
 * presentation forgets it (`forgetListingPresentationForAdd`, called by WebotServerContext).
 *
 * Its own module, importing no component: WebotServerContext imports the forget, and firestoreUtils
 * reaches WebotServerContext through the components it imports.
 */
const TTL_MS = 60 * 1000;
const lookups = new Map<string, { at: number; value: Promise<ListingPresentationEdits | null> }>();

export function lookupListingPresentationForAdd(
  userEmail: string,
  listingId: string,
  fallback: () => Promise<ListingPresentationEdits | null>
): Promise<ListingPresentationEdits | null> {
  const key = `${userEmail.trim().toLowerCase()}|${listingId}`;
  const cached = lookups.get(key);
  if (cached && Date.now() - cached.at < TTL_MS) return cached.value;
  const value = load(listingId, fallback);
  lookups.set(key, { at: Date.now(), value });
  return value;
}

/** The realtor just saved this listing's presentation: the next add must carry the new one. */
export function forgetListingPresentationForAdd(listingId: string): void {
  Array.from(lookups.keys())
    .filter((key) => key.endsWith(`|${listingId}`))
    .forEach((key) => lookups.delete(key));
}

async function load(
  listingId: string,
  fallback: () => Promise<ListingPresentationEdits | null>
): Promise<ListingPresentationEdits | null> {
  try {
    const currentUser = firebaseApp.auth().currentUser;
    if (!currentUser) throw new Error("no authenticated user yet");
    const token = await currentUser.getIdToken();
    const response = await fetch(
      `${WEBOT_SERVER_ENDPOINT}/api/v1/listing-presentations/${encodeURIComponent(listingId)}`,
      { headers: { Authorization: token } }
    );
    if (!response.ok) throw new Error(`listing-presentations failed with status ${response.status}`);
    return presentationEditsForAdd((await response.json()) as REST_schema.GetListingPresentationResponse);
  } catch (error) {
    console.error("Falling back to the per-minisite scan for the listing presentation", error);
    return fallback();
  }
}
